import { defineComponent as _defineComponent } from 'vue'
import ConfirmDeleteModal from "@/components/ui/modals/ConfirmDeleteModal.vue";
  import router from "@/router";
  import {
    ref,
    onMounted,
    computed,
    nextTick,
    watch,
  } from "vue";
  import { useStore } from "vuex";
  import { StageMapInterface } from "@/types/StageMapInterface";
  import { LotMapInterface } from "@/types/LotMapInterface";
  import { UserRoles } from "@/enums/UserRoles";
  import {
    getCustomerFacingLotStatusLabel,
    getLotCustomerMapStatusClass,
    getLotStatusClass,
  } from "@/helpers/status";
  import { LotStatus } from "@/enums/LotStatus";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import StageNavMobile from "@/components/StageNavMobile.vue";
  import { StageStatus } from "@/enums/StageStatus";
  import { eventTracking } from "@/helpers/googleAnalyticsEventTracking";
  import {
    EventActionName,
    EventByAction,
    EventByCategory,
    EventLabelByName,
  } from "@/types/EventTracking";
  import Hammer from "hammerjs";
  import type HammerInput from "hammerjs";
  import pinchZoomHammer from "@/helpers/estateMap/mapsZoom";
  import MapSidePanel from "./MapSidePanel.vue";
  import InfoWithIcon from "@/components/ui/InfoWithIcon.vue";
  import MapSideLegend from "./MapSideLegend.vue";
  import { LotInterface } from "@/types/LotInterface";
  import { BuilderInterface } from "@/types/BuilderInterface";
  import { Home } from "lucide-vue-next";
  import StageSvgMap from "@/components/maps/StageSvgMap.vue";
  import StageDocumentsModal from "@/components/ui/modals/StageDocumentsModal.vue";
  import { useEstateVersionRedirect } from "@/composables/useEstateVersionRedirect";
  import { getId } from "@/helpers/lot/homeAndLand";

  const mobileLayoutWidth = 680;
  interface BuilderMarkerColors {
    color: string;
    colorB?: string;
    colorC?: string;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'StageMap',
  props: {
    role: { type: String, default: () => UserRoles.LANDOWNER },
    viewType: { type: String, default: "" },
    isEmbedded: { type: Boolean },
    mapPage: { type: String, default: "" },
    zoom: {
      type: Number,
      default: 1,
    },
    builderView: { type: Boolean, required: false },
    actualStageQuote: { type: String, required: false, default: undefined },
    lotsByHouseDesigns: { type: Array, required: false, default: undefined },
    plansView: { type: Boolean, required: false, default: false },
    estateId: { type: String, required: false, default: undefined },
    hideMapSidePanelOptions: { type: Boolean, required: false },
    businessId: { type: String, required: false, default: undefined },
    isWaitlistEnabled: { type: Boolean, required: false, default: true },
  },
  emits: [
    "reserve",
    "changeView",
    "updateZoom",
    "onRegister",
    "onMessage",
    "selectLot",
    "changeViewMap",
    "onLotBuilderHover",
    "onMouseX",
    "onMouseY",
  ],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const store = useStore();

  const interactableStatuses = [
    LotStatus.AVAILABLE,
    LotStatus.HOLD,
    LotStatus.PRE_CONTRACT,
    LotStatus.EOI,
    LotStatus.CONTRACT_EXECUTED,
  ];

  const props = __props;
  const emits = __emit;
  const userId = router.currentRoute.value.params.userId;
  const availableLotsPopUpShow = ref(false);
  const { isDesktop } = useIsDesktop(mobileLayoutWidth);
  const estateId = computed(() => getEstateId());
  const stageId = computed(() => {
    if (props.builderView && props.actualStageQuote) {
      return props.actualStageQuote;
    }

    if (props.role === UserRoles.CUSTOMER) {
      return router.currentRoute.value.query.stageId as string;
    }

    return router.currentRoute.value.params.stageId as string;
  });
  const map = ref<StageMapInterface | null | "">(null);
  const lots = ref<LotMapInterface[]>([]);
  const builderColorsById = ref<Record<string, BuilderMarkerColors>>({});
  const activeBuilders = ref<BuilderInterface[]>([]);
  const isBuilderLegendOpen = ref(true);
  const showConfirmDeleteModal = ref(false);
  const showStageDocumentsModal = ref(false);
  const { checkEstateVersionAndRedirect } = useEstateVersionRedirect();

  const getAvailableLots = computed(() => {
    return lots.value.filter(
      (value) => value.lotId.status === LotStatus.AVAILABLE
    );
  });

  const activeBuilderLegendItems = computed(() => {
    const builderIds = getLotBuilderIds(lots.value);

    return builderIds
      .map((builderId) =>
        activeBuilders.value.find((builder) => builder._id === builderId)
      )
      .filter(Boolean) as BuilderInterface[];
  });

  const showBuilderLegend = computed(() => {
    return !props.builderView && activeBuilderLegendItems.value.length > 0;
  });

  const lotOnClick = ref("");

  const lotSidePanelHover = ref({
    value: false,
    id: "",
  });

  const getEstateId = () => {
    if (props.estateId) {
      return props.estateId;
    } else if (props.role === UserRoles.LANDOWNER) {
      return store.state.user.selectedEstateId;
    } else {
      return router.currentRoute.value.params.estateId;
    }
  };

  const isEmbeddedDesktop = computed(() => isDesktop.value);

  const getSvgItemWrClasses = (lot: LotMapInterface) => {
    return [
      {
        "not-clickable":
          props.role === UserRoles.CUSTOMER &&
          !interactableStatuses.includes(lot.lotId?.status),
      },
      {
        "lot-side-panel-hover":
          lotSidePanelHover.value?.value &&
          lotSidePanelHover.value?.id === lot.lotId?._id,
      },
      {
        "lot-side-panel":
          interactableStatuses.includes(lot.lotId?.status) && isEmbeddedDesktop,
      },
      {
        "lot-plans-view": props.plansView,
      },
      {
        "not-clickable-house-design": isNotClickableForBuilderView(lot),
      },
    ];
  };

  const getSvgItemPathClasses = (lot: LotMapInterface) => {
    return [
      {
        "selling-animation":
          interactableStatuses.includes(lot.lotId?.status) &&
          !isNotClickableForBuilderView(lot),
      },
      {
        "selling-animation-side-panel":
          lotSidePanelHover.value.value &&
          lotSidePanelHover.value?.id === lot.lotId?._id,
      },
      { "admin-highlight": props.role === UserRoles.SUPER_ADMIN },
    ];
  };

  const getSvgTooltipText = (lot: LotMapInterface) => {
    return isNotClickableForBuilderView(lot)
      ? ""
      : interactableStatuses.includes(lot.lotId.status) && isDesktop
      ? getTooltipText(lot)
      : "";
  };

  const getSvgItemStatusPathClasses = (lot: LotMapInterface) => [
    props.role === UserRoles.CUSTOMER
      ? getLotCustomerMapStatusClass(getClassForCustomer(lot))
      : getLotStatusClass(
          lot.lotId?.status === LotStatus.EOI
            ? LotStatus.PRE_CONTRACT
            : lot.lotId?.status
        ),
  ];

  const onSidePanelLotHover = (value: boolean, id: string) => {
    lotSidePanelHover.value = { value, id };
  };

  const onSidePanelLotClick = (lot: LotMapInterface) => {
    if (props.builderView) {
      emits("selectLot", lot);
    } else {
      onLot(lot);
    }
  };
  const isNotClickableForBuilderView = (lot: LotMapInterface) => {
    return (
      props.builderView &&
      !props.lotsByHouseDesigns.some((design) => design._id === lot.lotId?._id)
    );
  };

  const handleLotClickIfClickable = (lot: LotMapInterface) => {
    if (!isNotClickableForBuilderView(lot)) {
      onLot(lot);
    }
  };

  const onLotBuilderHover = (value: boolean) => {
    emits("onLotBuilderHover", value);
  };

  const handleMouseMove = (e: MouseEvent) => {
    emits("onMouseX", e.clientX + 15);
    emits("onMouseY", e.clientY + 5);
  };

  const hoverLotId = ref("");

  const onLotHover = (id: string) => {
    hoverLotId.value = id;
  };

  const onLotLeave = () => {
    hoverLotId.value = "";
  };

  const getTooltipText = (lot: LotMapInterface) => {
    const status =
      props.role === UserRoles.CUSTOMER
        ? getCustomerFacingLotStatusLabel(lot.lotId.status)
        : lot.lotId.status;
    const text = `<div><strong>Lot ${lot.lotId.title}
    <br>${status.toUpperCase()}</strong><br>
    <p class="lot-size">${lot.lotId.size}m<sup>2</sup></p>
    <p>Click to view more</p></div>`;

    return text;
  };

  onMounted(() => {
    getMapWithLots();
    checkEstateVersionAndRedirect(estateId.value, props.role);
  });

  const onStage = (stage: StageMapInterface) => {
    const embed = props.isEmbedded.toString();

    router
      .replace({
        path: router.currentRoute.value.path,
        query: { stageId: stage.stageId._id, embed },
      })
      .then(() => {
        getMapWithLots();
      });
  };

  const toMasterplan = () => {
    if (props.builderView) {
      emits("changeViewMap");
    } else if (props.plansView) {
      router.push({ name: "LandingPlans" });
    } else {
      back();
    }
  };

  const getMapWithLots = () => {
    getMap();
    getLots().then(() => {
      const lotId = router.currentRoute.value.query.lotId;

      if (lotId) {
        const lotForModal = lots.value.find(
          (value) => value.lotId._id === lotId
        );

        if (lotForModal) {
          emits("reserve", lotForModal);
        }
      }
    });
    showAvailableLotsPopUp();
  };

  function getMap() {
    store
      .dispatch("stage/getStageMap", stageId.value)
      .then((res) => {
        map.value = res.data;
      })
      .catch((error) => {
        if (error.response?.data?.statusCode === 404) {
          map.value = "";
        }
      });
  }
  function getLots() {
    return store
      .dispatch("lot/getLotsMap", { stageId: stageId.value })
      .then((res) => {
        lots.value = res.data;
        loadBuilderColors(res.data);
      });
  }

  function getLotBuilderIds(stageLots: LotMapInterface[]) {
    return Array.from(
      new Set(
        stageLots
          .filter(shouldShowLotHouseMarker)
          .flatMap((lot) => lot.lotId?.builders || [])
          .map((builder) => getId(builder.builderId))
          .filter(Boolean)
      )
    );
  }

  function shouldShowLotHouseMarker(lot: LotMapInterface) {
    return Boolean(lot.houseMarker && lot.lotId?.isBuildersEnabled);
  }

  async function loadBuilderColors(stageLots: LotMapInterface[]) {
    const builderIds = getLotBuilderIds(stageLots);

    if (!builderIds.length) {
      builderColorsById.value = {};
      activeBuilders.value = [];
      return;
    }

    try {
      const res = await store.dispatch("builder/getBuildersByIds", builderIds);

      activeBuilders.value = res.data;
      builderColorsById.value = Object.fromEntries(
        (res.data as BuilderInterface[]).map((builder) => [
          builder._id,
          {
            color: builder.color,
            colorB: builder.colorB,
            colorC: builder.colorC,
          },
        ])
      );
    } catch {
      builderColorsById.value = {};
      activeBuilders.value = [];
    }
  }
  function toCreateMap() {
    router.push({
      name: "AdminStageMapCreate",
      params: { userId, estateId: estateId.value, stageId: stageId.value },
    });
  }
  function toEditMap() {
    router.push({
      name: "AdminStageMapUpdate",
      params: { userId, estateId: estateId.value, stageId: stageId.value },
    });
  }
  function deleteMap() {
    store.dispatch("stage/deleteStageMap", map.value._id).then(() => {
      getMap();
    });
  }

  function onLot(lot: LotMapInterface) {
    trackLotClickEvent(lot);

    if (props.builderView) {
      emits("selectLot", lot);
      return;
    }

    if (props.role === UserRoles.LANDOWNER) {
      router.push({
        name: "LotActions",
        params: {
          stageId: stageId.value,
          lotId: lot.lotId._id,
        },
      });
      return;
    }

    if (props.role === UserRoles.SUPER_ADMIN) {
      router.push({
        name: "AdminLot",
        params: {
          userId,
          estateId: estateId.value,
          stageId: stageId.value,
          lotId: lot.lotId._id,
        },
      });
      return;
    }

    if (
      props.role === UserRoles.CUSTOMER &&
      interactableStatuses.includes(lot.lotId.status)
    ) {
      emits("reserve", lot);
    }
  }

  function back() {
    const embed = props.isEmbedded.toString();

    router.replace({
      path: router.currentRoute.value.path,
      query: { embed, businessId: props.businessId },
    });
  }

  const showAvailableLotsPopUp = () => {
    setTimeout(() => {
      availableLotsPopUpShow.value = true;
    }, 3000);
  };

  const getClassForCustomer = (lot: LotMapInterface): LotStatus => {
    if (isNotClickableForBuilderView(lot)) {
      return LotStatus.PRE_CONTRACT;
    }

    if (lot.lotId?.status === LotStatus.OFFLINE) {
      return lot.lotId?.offlineSale?.salePhase as unknown as LotStatus;
    }

    if (
      lot.lotId?.status === LotStatus.HOLD ||
      lot.lotId?.status === LotStatus.PRE_CONTRACT
    ) {
      return LotStatus.HOLD;
    }

    return lot.lotId?.status;
  };
  const trackLotClickEvent = (lot: LotMapInterface) => {
    eventTracking({
      actionsName: EventActionName.LOT_ON_CLICK,
      eventByCategory: EventByCategory.ON_CLICK,
      eventLabelByName: EventLabelByName.CLICK_LOT,
      eventByAction: EventByAction.CLICK,
      estateById: estateId.value,
      stageById: stageId.value,
      stageTitle: lot.stageId.title,
      lotById: lot.lotId._id,
      lotTitle: lot.lotId.title,
    });
  };

  const isDragging = ref(false);
  const svgStartPosition = ref({ x: 0, y: 0 });
  const svgStartScrollPosition = ref({ x: 0, y: 0 });
  const svgContainerMap = ref();

  const startDragging = (event: MouseEvent) => {
    if (props.zoom !== 1) {
      isDragging.value = true;
      svgStartPosition.value = { x: event.clientX, y: event.clientY };
      svgStartScrollPosition.value = {
        x: svgContainerMap.value.scrollLeft,
        y: svgContainerMap.value.scrollTop,
      };
      svgContainerMap.value.style.cursor = "grabbing";
    }
  };
  const handleDragging = (event: MouseEvent) => {
    event.preventDefault();

    if (isDragging.value) {
      const deltaX = event.clientX - svgStartPosition.value.x;
      const deltaY = event.clientY - svgStartPosition.value.y;

      svgContainerMap.value.scrollLeft =
        svgStartScrollPosition.value.x - deltaX;
      svgContainerMap.value.scrollTop = svgStartScrollPosition.value.y - deltaY;
    }
  };
  const stopDragging = () => {
    if (props.zoom !== 1) {
      svgContainerMap.value.style.cursor = "grab";
    } else {
      svgContainerMap.value.style.cursor = "default";
    }

    isDragging.value = false;
  };

  onMounted(() => {
    const mc = new Hammer(svgContainerMap.value);

    mc.get("pinch").set({ enable: true });
    mc.on("pinch", (event: HammerInput) => {
      pinchZoomHammer(event.scale, (delta) => emits("updateZoom", delta));
    });

    mc.on("panstart", (event: HammerInput) => {
      svgStartPosition.value = { x: event.center.x, y: event.center.y };
      svgStartScrollPosition.value = {
        x: svgContainerMap.value.scrollLeft,
        y: svgContainerMap.value.scrollTop,
      };
    });

    mc.on("pan", (event: HammerInput) => {
      const deltaX = event.center.x - svgStartPosition.value.x;
      const deltaY = event.center.y - svgStartPosition.value.y;

      svgContainerMap.value.scrollLeft =
        svgStartScrollPosition.value.x - deltaX;
      svgContainerMap.value.scrollTop = svgStartScrollPosition.value.y - deltaY;
    });
  });

const __returned__ = { store, interactableStatuses, props, emits, userId, mobileLayoutWidth, availableLotsPopUpShow, isDesktop, estateId, stageId, map, lots, builderColorsById, activeBuilders, isBuilderLegendOpen, showConfirmDeleteModal, showStageDocumentsModal, checkEstateVersionAndRedirect, getAvailableLots, activeBuilderLegendItems, showBuilderLegend, lotOnClick, lotSidePanelHover, getEstateId, isEmbeddedDesktop, getSvgItemWrClasses, getSvgItemPathClasses, getSvgTooltipText, getSvgItemStatusPathClasses, onSidePanelLotHover, onSidePanelLotClick, isNotClickableForBuilderView, handleLotClickIfClickable, onLotBuilderHover, handleMouseMove, hoverLotId, onLotHover, onLotLeave, getTooltipText, onStage, toMasterplan, getMapWithLots, getMap, getLots, getLotBuilderIds, shouldShowLotHouseMarker, loadBuilderColors, toCreateMap, toEditMap, deleteMap, onLot, back, showAvailableLotsPopUp, getClassForCustomer, trackLotClickEvent, isDragging, svgStartPosition, svgStartScrollPosition, svgContainerMap, startDragging, handleDragging, stopDragging, ConfirmDeleteModal, get router() { return router }, ref, onMounted, computed, nextTick, watch, get useStore() { return useStore }, get StageMapInterface() { return StageMapInterface }, get LotMapInterface() { return LotMapInterface }, get UserRoles() { return UserRoles }, get getCustomerFacingLotStatusLabel() { return getCustomerFacingLotStatusLabel }, get getLotCustomerMapStatusClass() { return getLotCustomerMapStatusClass }, get getLotStatusClass() { return getLotStatusClass }, get LotStatus() { return LotStatus }, get useIsDesktop() { return useIsDesktop }, StageNavMobile, get StageStatus() { return StageStatus }, get eventTracking() { return eventTracking }, get EventActionName() { return EventActionName }, get EventByAction() { return EventByAction }, get EventByCategory() { return EventByCategory }, get EventLabelByName() { return EventLabelByName }, get Hammer() { return Hammer }, get pinchZoomHammer() { return pinchZoomHammer }, MapSidePanel, InfoWithIcon, MapSideLegend, get LotInterface() { return LotInterface }, get BuilderInterface() { return BuilderInterface }, get Home() { return Home }, StageSvgMap, StageDocumentsModal, get useEstateVersionRedirect() { return useEstateVersionRedirect }, get getId() { return getId } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})